/**
 * SincronizacionOdoo.jsx · v101.162.0
 * Integraciones › Sincronización con Odoo: OTs desincronizadas por categoría, reenvío manual de cancelaciones y
 * trazabilidad completa de cualquier OT (cada acción, cambio de estado y llamada a Odoo / red con su resultado).
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import {
  RefreshCw, Loader2, Search, CheckCircle2, XCircle, Clock, AlertTriangle, Send, History, X, ExternalLink, Download,
} from "lucide-react";
import Layout from "../components/Layout";
import api from "../lib/api";

const BASE = "/v1/integraciones/sincronizacion-odoo";
const KIND = {
  ok: { bg: "#D1FAE5", fg: "#065F46", Icon: CheckCircle2 },
  error: { bg: "#FEE2E2", fg: "#991B1B", Icon: XCircle },
  pending: { bg: "#FEF3C7", fg: "#92400E", Icon: Clock },
  warn: { bg: "#FFEDD5", fg: "#9A3412", Icon: AlertTriangle },
};
const EST_CANCEL = { nunca_enviada: "Nunca enviada", pendiente_sync: "Pendiente", error_negocio: "Rechazada por Odoo",
  error_validacion: "Datos incompletos", completado: "Enviada OK" };
const fmt = (v) => (v ? new Date(v).toLocaleString("es-EC") : "—");

function SyncPill({ s }) {
  if (!s) return null;
  const k = KIND[s.kind] || KIND.warn;
  return (
    <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold whitespace-nowrap"
      style={{ background: k.bg, color: k.fg }}>
      <k.Icon size={11} />{s.label}
    </span>
  );
}

function Trazabilidad({ ot, onClose }) {
  const [data, setData] = useState(null);
  const [filtro, setFiltro] = useState("todo");
  useEffect(() => {
    api.get(`${BASE}/trazabilidad/${encodeURIComponent(ot)}`)
      .then(({ data: d }) => setData(d))
      .catch((e) => { toast.error(e?.response?.data?.detail || "No se pudo cargar la trazabilidad"); onClose(); });
  }, [ot, onClose]);
  const ev = (data?.eventos || []).filter((e) => filtro === "todo" || (filtro === "odoo" ? e.origen === "Odoo" : e.origen !== "Odoo"));
  const o = data?.ot || {};
  return (
    <div className="fixed inset-0 z-[4000] bg-black/40 flex justify-end" onClick={onClose} data-testid="traza-drawer">
      <div className="w-full max-w-2xl h-full bg-white overflow-y-auto p-4 space-y-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-2">
          <div>
            <div className="text-xs text-slate-500 font-bold uppercase tracking-wider">Trazabilidad de la OT</div>
            <div className="text-lg font-bold text-[#0A3747]">{o.task_number || ot}</div>
            <div className="text-xs text-slate-500">{o.cliente || ""}{o.sucursal ? ` · ${o.sucursal}` : ""}</div>
          </div>
          <div className="flex items-center gap-2">
            {o.odoo_task_id && (
              <Link to={`/ot-smart/${o.odoo_task_id}`} className="text-xs font-bold text-[#0A3747] flex items-center gap-1 underline">
                Abrir OT <ExternalLink size={12} />
              </Link>
            )}
            <button onClick={onClose} className="p-1 rounded hover:bg-slate-100"><X size={16} /></button>
          </div>
        </div>
        {!data ? (
          <div className="py-10 text-center text-xs text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando…</div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-2 text-[11px]" data-testid="traza-resumen">
              <div className="rounded-lg border p-2"><div className="text-slate-500">Estado en AlfaField</div><b>{o.estado_alfafield || "—"}</b></div>
              <div className="rounded-lg border p-2"><div className="text-slate-500">Etapa en Odoo</div><b>{o.etapa_odoo || "—"}</b></div>
              <div className="rounded-lg border p-2"><div className="text-slate-500">Sincronización</div><SyncPill s={o.sync} /></div>
              <div className="rounded-lg border p-2"><div className="text-slate-500">Última lectura desde Odoo</div><b>{fmt(o.ultima_lectura_odoo)}</b></div>
              <div className="rounded-lg border p-2 col-span-2">
                <div className="text-slate-500">Cierre a Odoo</div>
                <b>{o.cierre?.estado || "sin envío"}</b> · {fmt(o.cierre?.at)}
                {o.cierre?.mensaje && <div className="text-slate-600 mt-0.5">{o.cierre.mensaje}</div>}
              </div>
              <div className="rounded-lg border p-2 col-span-2">
                <div className="text-slate-500">Cancelación a Odoo</div>
                <b>{o.cancelacion?.estado || "sin envío"}</b> · {fmt(o.cancelacion?.at)}
                {o.cancelacion?.mensaje && <div className="text-slate-600 mt-0.5">{o.cancelacion.mensaje}</div>}
              </div>
            </div>
            <div className="flex items-center gap-1.5 text-[11px]">
              {[["todo", "Todo"], ["odoo", "Solo Odoo"], ["af", "AlfaField / red"]].map(([k, l]) => (
                <button key={k} onClick={() => setFiltro(k)}
                  className={`px-2 py-1 rounded-md border font-bold ${filtro === k ? "bg-[#0A3747] text-white border-[#0A3747]" : "border-slate-200 text-slate-600"}`}>{l}</button>
              ))}
              <span className="ml-auto text-slate-400">{ev.length} eventos</span>
            </div>
            <ol className="relative border-l border-slate-200 ml-2 space-y-2" data-testid="traza-eventos">
              {ev.map((e, i) => (
                <li key={i} className="ml-3">
                  <span className="absolute -left-1.5 mt-1.5 w-3 h-3 rounded-full border-2 border-white"
                    style={{ background: !e.ok ? "#DC2626" : e.origen === "Odoo" ? "#7C3AED" : "#0A3747" }} />
                  <div className="text-[10px] text-slate-400">{fmt(e.at)} · {e.origen}{e.por ? ` · ${e.por}` : ""}</div>
                  <div className={`text-xs font-bold ${e.ok ? "text-[#0A3747]" : "text-red-700"}`}>
                    {e.titulo}{e.http ? ` · HTTP ${e.http}` : ""}{!e.ok ? " · FALLÓ" : ""}
                  </div>
                  {e.detalle && <div className="text-[11px] text-slate-600 whitespace-pre-wrap break-words">{e.detalle}</div>}
                </li>
              ))}
              {ev.length === 0 && <li className="ml-3 text-xs text-slate-400 italic">Sin eventos</li>}
            </ol>
          </>
        )}
      </div>
    </div>
  );
}

export default function SincronizacionOdoo() {
  const [res, setRes] = useState(null);
  const [cat, setCat] = useState("cancel_no_sync");
  const [list, setList] = useState(null);
  const [q, setQ] = useState("");
  const [branch, setBranch] = useState("");
  const [sel, setSel] = useState([]);
  const [busy, setBusy] = useState(false);
  const [traza, setTraza] = useState(null);
  const [buscarOt, setBuscarOt] = useState("");
  // v101.178.0 · Reporte de devolución de equipos (Excel)
  const [repOpen, setRepOpen] = useState(false);
  const [repDesde, setRepDesde] = useState("");
  const [repSolo, setRepSolo] = useState(false);
  const [repBusy, setRepBusy] = useState(false);
  const descargarDevoluciones = async () => {
    setRepBusy(true);
    try {
      const resp = await api.get("/v1/integraciones/sincronizacion-odoo/reporte-devoluciones.xlsx", {
        params: { desde: repDesde || undefined, solo_pendientes: repSolo || undefined },
        responseType: "blob", timeout: 300000,
      });
      const url = URL.createObjectURL(resp.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `reporte_devoluciones_${new Date().toISOString().slice(0, 10)}.xlsx`;
      a.click();
      URL.revokeObjectURL(url);
      const h = resp.headers || {};
      const tot = h["x-resumen-total"], pen = h["x-resumen-pendientes"];
      toast.success(`Reporte descargado: ${tot ?? "?"} devoluciones · ${pen ?? "?"} con OT cerrada sin registrar en Odoo`
        + (h["x-espejo-odoo"] === "0" ? ". Sin acceso al espejo de Odoo: la ubicación actual de cada equipo no se pudo consultar." : ""));
      setRepOpen(false);
    } catch (e) {
      let msg = "No se pudo generar el reporte de devoluciones";
      try {
        if (e?.response?.data instanceof Blob) {
          const t = JSON.parse(await e.response.data.text());
          if (t?.detail) msg = `${msg}: ${t.detail}`;
        }
      } catch (_) { /* */ }
      toast.error(msg);
    } finally {
      setRepBusy(false);
    }
  };
  const closeTraza = useCallback(() => setTraza(null), []);

  const loadRes = useCallback(async () => {
    try { const { data } = await api.get(`${BASE}/resumen`, { params: { branch: branch || undefined } }); setRes(data); }
    catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar el resumen"); }
  }, [branch]);
  const loadList = useCallback(async () => {
    setList(null); setSel([]);
    try {
      const { data } = await api.get(`${BASE}/lista`, { params: { categoria: cat, q: q || undefined, branch: branch || undefined, limit: 200 } });
      setList(data);
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo cargar la lista"); setList({ items: [], total: 0 }); }
  }, [cat, q, branch]);

  useEffect(() => { loadRes(); }, [loadRes]);
  useEffect(() => { loadList(); }, [loadList]);

  const reenviar = async () => {
    if (!sel.length) return;
    if (!window.confirm(`¿Enviar a Odoo la cancelación de ${sel.length} OT(s)? Odoo cancelará esas tareas.`)) return;
    setBusy(true);
    try {
      const { data } = await api.post(`${BASE}/reenviar-cancelacion`, { odoo_task_ids: sel });
      toast.success(`Cancelaciones enviadas: ${data.ok} de ${data.enviadas} OK`);
      const bad = (data.items || []).filter((x) => !x.ok);
      if (bad.length) toast.error(`${bad.length} no se cancelaron en Odoo: ${bad.slice(0, 3).map((x) => x.mensaje).join(" · ")}`);
      await loadRes(); await loadList();
    } catch (e) { toast.error(e?.response?.data?.detail || "No se pudo reenviar"); }
    finally { setBusy(false); }
  };

  const items = list?.items || [];
  const canResend = cat === "cancel_no_sync" && res?.can_edit;
  const toggle = (id) => setSel((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id].slice(0, 30)));

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-4 space-y-4" data-testid="sync-odoo-page">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h1 className="text-lg font-bold text-[#0A3747]">Sincronización con Odoo</h1>
            <p className="text-xs text-slate-500 max-w-2xl">
              OTs cuyo estado en AlfaField no coincide con Odoo, y la trazabilidad completa de cualquier OT: cada acción,
              cambio de estado, técnico o agenda, y cada envío a Odoo o a la red con su resultado.
            </p>
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <History size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
              <input value={buscarOt} onChange={(e) => setBuscarOt(e.target.value)} placeholder="Trazabilidad: N° de OT"
                onKeyDown={(e) => { if (e.key === "Enter" && buscarOt.trim()) setTraza(buscarOt.trim()); }}
                className="pl-7 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-48" data-testid="traza-buscar" />
            </div>
<div className="relative">
              <button onClick={() => setRepOpen((v) => !v)} data-testid="btn-reporte-devoluciones"
                className="px-3 py-1.5 rounded-lg border border-[#0A3747]/20 bg-[#0A3747] text-white text-xs font-bold flex items-center gap-1.5 hover:bg-[#0d4659]">
                <Download size={12} /> Reporte de devoluciones
              </button>
              {repOpen && (
                <div className="absolute right-0 mt-2 w-80 z-30 rounded-2xl border border-slate-200 bg-white shadow-xl p-4 space-y-3"
                  data-testid="panel-reporte-devoluciones">
                  <div>
                    <div className="text-sm font-bold text-[#0A3747]">Devolución de equipos (Excel)</div>
                    <p className="text-[11px] text-slate-500 mt-0.5">
                      Todas las devoluciones con su detalle y las de OTs cerradas que NO están registradas en Odoo
                      (por sucursal y técnico).
                    </p>
                  </div>
                  <label className="block text-[11px] text-slate-600">
                    Desde (opcional)
                    <input type="date" value={repDesde} onChange={(e) => setRepDesde(e.target.value)}
                      className="mt-1 w-full px-2 py-1.5 rounded-lg border border-slate-200 text-xs" data-testid="rep-dev-desde" />
                  </label>
                  <label className="flex items-center gap-2 text-[11px] text-slate-600">
                    <input type="checkbox" checked={repSolo} onChange={(e) => setRepSolo(e.target.checked)} />
                    Solo las cerradas sin registrar en Odoo
                  </label>
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setRepOpen(false)} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs text-slate-600">
                      Cancelar
                    </button>
                    <button onClick={descargarDevoluciones} disabled={repBusy} data-testid="rep-dev-descargar"
                      className="px-3 py-1.5 rounded-lg bg-[#0A3747] text-white text-xs font-bold flex items-center gap-1.5 disabled:opacity-60">
                      {repBusy ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />}
                      {repBusy ? "Generando…" : "Descargar"}
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button onClick={() => { loadRes(); loadList(); }} className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs font-bold text-slate-600 flex items-center gap-1.5">
              <RefreshCw size={12} /> Actualizar
            </button>
          </div>
        </div>

        {res && res.cancelar_envia_a_odoo === false && (
          <div className="rounded-xl bg-amber-50 border border-amber-200 px-3 py-2 text-xs text-amber-800 flex items-start gap-2" data-testid="sync-cancel-off">
            <AlertTriangle size={14} className="shrink-0 mt-0.5" />
            <span>La validación <b>«Cancelar también en Odoo»</b> está APAGADA (Centro de Validaciones): al cancelar en AlfaField
              la tarea NO se cancela en Odoo. Por eso se acumulan OTs «Cancelada en AlfaField, NO en Odoo». Se pueden enviar a
              mano desde aquí o prender esa validación.</span>
          </div>
        )}

        <div className="grid grid-cols-2 md:grid-cols-3 gap-2" data-testid="sync-cards">
          {(res?.categorias || []).map((c) => (
            <button key={c.code} onClick={() => setCat(c.code)} title={c.ayuda}
              className={`text-left rounded-xl border px-3 py-2 transition ${cat === c.code ? "border-[#0A3747] ring-1 ring-[#0A3747] bg-white" : "border-slate-200 bg-white hover:bg-slate-50"}`}
              data-testid={`sync-cat-${c.code}`}>
              <div className="text-[11px] text-slate-500 font-bold">{c.label}</div>
              <div className={`text-2xl font-black ${c.total ? "text-red-700" : "text-emerald-700"}`}>{c.total ?? "—"}</div>
            </button>
          ))}
        </div>

        {cat === "cancel_no_sync" && (res?.cancelaciones_por_estado || []).length > 0 && (
          <div className="flex items-center gap-2 flex-wrap text-[11px]" data-testid="sync-cancel-estados">
            <span className="text-slate-500 font-bold">Por qué no está cancelada en Odoo:</span>
            {res.cancelaciones_por_estado.map((x) => (
              <span key={x.estado} className="px-2 py-0.5 rounded-full bg-slate-100 text-slate-700 font-bold">
                {EST_CANCEL[x.estado] || x.estado}: {x.total}
              </span>
            ))}
          </div>
        )}

        <div className="flex items-center gap-2 flex-wrap">
          <div className="relative">
            <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-slate-400" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Buscar OT o cliente"
              className="pl-7 pr-3 py-1.5 rounded-lg border border-slate-200 text-xs w-52" />
          </div>
          <input value={branch} onChange={(e) => setBranch(e.target.value)} placeholder="Sucursal"
            className="px-3 py-1.5 rounded-lg border border-slate-200 text-xs w-40" />
          {canResend && (
            <button onClick={reenviar} disabled={!sel.length || busy}
              className="ml-auto px-3 py-1.5 rounded-lg bg-[#0A3747] text-[#E0E621] text-xs font-bold disabled:opacity-50 flex items-center gap-1.5"
              data-testid="sync-reenviar">
              {busy ? <Loader2 size={12} className="animate-spin" /> : <Send size={12} />}
              Enviar cancelación a Odoo ({sel.length})
            </button>
          )}
        </div>

        <div className="bg-white border border-slate-200 rounded-xl overflow-x-auto" data-testid="sync-lista">
          {!list ? (
            <div className="py-10 text-center text-xs text-slate-500"><Loader2 size={14} className="animate-spin inline mr-2" />Cargando…</div>
          ) : (
            <table className="w-full text-xs min-w-[860px]">
              <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] tracking-wider">
                <tr>
                  {canResend && <th className="px-2 py-2 w-6"></th>}
                  <th className="px-3 py-2 text-left">OT</th>
                  <th className="px-3 py-2 text-left">Cliente / sucursal</th>
                  <th className="px-3 py-2 text-left">AlfaField</th>
                  <th className="px-3 py-2 text-left">Odoo</th>
                  <th className="px-3 py-2 text-left">Sincronización / último mensaje</th>
                  <th className="px-3 py-2"></th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {items.map((x) => (
                  <tr key={x.odoo_task_id}>
                    {canResend && (
                      <td className="px-2 py-2">
                        <input type="checkbox" checked={sel.includes(x.odoo_task_id)} onChange={() => toggle(x.odoo_task_id)} />
                      </td>
                    )}
                    <td className="px-3 py-2 font-mono font-bold text-[#0A3747]">{x.task_number || x.odoo_task_id}</td>
                    <td className="px-3 py-2"><div className="truncate max-w-[220px]">{x.client_name || "—"}</div>
                      <div className="text-[10px] text-slate-400">{x.branch_name || ""}</div></td>
                    <td className="px-3 py-2">{x.internal_status || "—"}</td>
                    <td className="px-3 py-2">{x.stage_name || "—"}</td>
                    <td className="px-3 py-2">
                      <SyncPill s={x.sync} />
                      <div className="text-[10.5px] text-slate-500 max-w-[340px] break-words">
                        {(cat.startsWith("cancel") ? x.cancel_event_message : x.close_event_message) || ""}
                      </div>
                      <div className="text-[10px] text-slate-400">{fmt(cat.startsWith("cancel") ? x.cancel_event_at : x.close_event_at || x.updated_at)}</div>
                    </td>
                    <td className="px-3 py-2 text-right whitespace-nowrap">
                      <button onClick={() => setTraza(String(x.odoo_task_id))} className="text-[11px] font-bold text-[#0A3747] underline"
                        data-testid={`sync-traza-${x.odoo_task_id}`}>Trazabilidad</button>
                    </td>
                  </tr>
                ))}
                {items.length === 0 && (
                  <tr><td colSpan={7} className="py-8 text-center text-emerald-700 font-bold">Sin OTs en esta categoría</td></tr>
                )}
              </tbody>
            </table>
          )}
          {list && list.total > items.length && (
            <div className="px-3 py-2 text-[11px] text-slate-500">Mostrando {items.length} de {list.total}. Use el buscador o la sucursal para acotar.</div>
          )}
        </div>
      </div>
      {traza && <Trazabilidad ot={traza} onClose={closeTraza} />}
    </Layout>
  );
}
